import { useState } from 'react'
import { PencilSquareIcon, TrashIcon, PlusIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { showAlert } from 'services/alerts.service'
import { makeRequest } from 'services/request.service'
import TableComponent from 'components/common/TableComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import ActionCardComponent from 'components/common/ActionCardComponent'
import DeviceGroupModal from 'components/modals/DeviceGroupModal'
import ModalComponent from 'components/common/ModalComponent'

const DevicesManagement = ({
  devicesGroups,
  onRefreshDevicesGroups,
  user
}) => {
  const { t } = useTranslation()
  const [state, setState] = useState({
    searchTermDeviceGroups: '',
    isOpenDeviceGroupModal: false,
    selectedDeviceGroupToEdit: null,
    showDeleteConfirm: false,
    pendingDelete: null
  })

  const { isOpenDeviceGroupModal } = state

  // Helper for auth headers
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('secret')}`,
    'Content-Type': 'application/json'
  })

  // PUT/POST devices groups
  const handleCreateDeviceGroup = async data => {
    try {
      const isEditing = !!state?.selectedDeviceGroupToEdit?.deviceGroupId

      const method = isEditing ? 'PUT' : 'POST'
      const url = isEditing
        ? `/management/devices/groups/${state.selectedDeviceGroupToEdit.deviceGroupId}`
        : `/management/devices/groups/${user.farmId}`

      const response = await makeRequest({
        method,
        url,
        data,
        headers: getAuthHeaders(),
        alertResult: true
      })

      if (response.status === 200) {
        handleToggleDeviceGroupModal()
        await onRefreshDevicesGroups()
      }
    } catch (error) {
      showAlert({ message: t('mgmt.deviceGroups.errSave'), icon: 'error' })
      console.error('Error saving device group:', error)
    }
  }

  // Delete device group (bottom table)
  const handleDeleteDeviceGroup = (deviceGroup) => {
    setState(s => ({ ...s, showDeleteConfirm: true, pendingDelete: deviceGroup }))
  }

  const confirmDeleteDeviceGroup = async () => {
    const deviceGroup = state.pendingDelete
    setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))
    try {
      const response = await makeRequest({
        method: 'PATCH',
        url: `/management/devices/groups/${deviceGroup.deviceGroupId}`,
        headers: getAuthHeaders(),
        data: {},
        alertResult: true
      })

      if (response.status === 200) {
        await onRefreshDevicesGroups()
        showAlert({ message: t('mgmt.deviceGroups.deletedOk'), icon: 'success' })
      }
    } catch (err) {
      console.error('Error deleting device group:', err)
      showAlert({ message: t('mgmt.deviceGroups.errDelete'), icon: 'error' })
    }
  }

  const handleToggleDeviceGroupModal = (deviceGroup = null) => setState(s => ({
    ...s,
    isOpenDeviceGroupModal: !isOpenDeviceGroupModal,
    selectedDeviceGroupToEdit: deviceGroup
  }))

  // Filter device groups for bottom table
  const filteredDeviceGroups = devicesGroups.filter(group => {
    const searchTerm = state.searchTermDeviceGroups.toLowerCase()
    return group.deviceGroupId.toString().includes(searchTerm) ||
           group.groupName.toLowerCase().includes(searchTerm)
  })

  const devicesTableData = {
    columns: [
      { key: 'deviceGroupId', labelKey: 'mgmt.deviceGroups.cols.deviceId' },
      { key: 'groupName', labelKey: 'mgmt.deviceGroups.cols.groupName' },
      {
        key: 'actions',
        labelKey: 'mgmt.deviceGroups.cols.manage',
        cell: (value, row) => (
          <div className='flex items-center gap-2'>
            <button
              title={t('common.edit')}
              onClick={() => handleToggleDeviceGroupModal(row)}
              className='p-1 rounded transition'
            >
              <PencilSquareIcon className='w-5 h-5 text-blue-500' />
            </button>
            <button onClick={() => handleDeleteDeviceGroup(row)} title={t('mgmt.deviceGroups.deleteBtnTitle')} className='p-1 rounded transition'>
              <TrashIcon className='w-5 h-5 text-red-500' />
            </button>
          </div>
        )
      }
    ],
    rows: filteredDeviceGroups
  }

  return (
    <>
      {/* Device Groups Management */}
      <ActionCardComponent>
        <input
          type='text'
          placeholder={t('mgmt.deviceGroups.search')}
          value={state.searchTermDeviceGroups}
          onChange={(e) => setState(s => ({ ...s, searchTermDeviceGroups: e.target.value.toLowerCase() }))}
          className='search-input w-full mt-1 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
        <ButtonComponent onClick={handleToggleDeviceGroupModal}>
          <PlusIcon className='w-5 h-5' />
          {t('mgmt.deviceGroups.new')}
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent tableData={devicesTableData} defaultSortColumn={devicesTableData.columns[0]?.key} />

      {/* Modals */}
      <DeviceGroupModal
        key={isOpenDeviceGroupModal ? 'open' : 'closed'}
        isOpen={isOpenDeviceGroupModal}
        handleSave={handleCreateDeviceGroup}
        handleClose={handleToggleDeviceGroupModal}
        selectedDevice={state.selectedDeviceGroupToEdit}
      />

      <ModalComponent
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
        title={t('mgmt.deviceGroups.deleteTitle')}
        maxWidth='sm'
        hideDefaultFooter
      >
        <div className='flex flex-col items-center gap-4 py-2'>
          <ExclamationTriangleIcon className='w-12 h-12 text-red-400' />
          <p className='text-sm text-gray-700 text-center'>
            {t('mgmt.deviceGroups.removeConfirm', { name: state.pendingDelete?.groupName ?? '' })}
            <br />
            <span className='text-gray-500'>{t('mgmt.common.confirmDelete')}</span>
          </p>
          <div className='flex justify-end gap-3 w-full mt-2'>
            <button
              type='button'
              onClick={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 transition'
            >
              {t('mgmt.common.cancel')}
            </button>
            <button
              type='button'
              onClick={confirmDeleteDeviceGroup}
              className='px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition'
            >
              {t('mgmt.common.delete')}
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  )
}

export default DevicesManagement
